import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Product } from '@/types/product';
import { useLanguage } from '@/context/LanguageContext';

interface ProductCardProps {
  product: Product;
  onClick?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
  const { addToCart } = useCart();
  const { tl } = useLanguage();
  
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  return (
    <div 
      className="group bg-white rounded-md overflow-hidden card-shadow animate-scale-in"
      onClick={onClick}
    >
      {/* Product Image */}
      <div className="relative overflow-hidden">
        <img 
          src={product.images[0]} 
          alt={product.name}
          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Overlay with quick actions */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-2">
          <button 
            onClick={handleAddToCart}
            className="bg-white text-black hover:bg-furniture-taupe hover:text-white hover:bg-black p-3 rounded-full transition-colors"
          >
            <ShoppingCart size={20} />
          </button>
          <Link 
            to={`/product/${product.id}`} 
            className="bg-white text-black hover:bg-furniture-taupe hover:text-white hover:bg-black p-3 rounded-full transition-colors"
          >
            <Eye size={20} />
          </Link>
        </div>
        
        {/* Tags */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {product.discount != null && product.discount > 0 && (
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
              -{product.discount}%
            </span>
          )}
          {product.new && (
            <span className="bg-black text-white text-xs font-bold px-2 py-1 rounded">
              {tl('NOUVEAU', 'NEW', 'NUOVO')}
            </span>
          )}
          {product.collection_2026 && (
            <span className="bg-furniture-gold text-white text-xs font-bold px-2 py-1 rounded">
              {tl('COLLECTION 2026', '2026 COLLECTION', 'COLLEZIONE 2026')}
            </span>
          )}
          {product.featured && (
            <span className="bg-furniture-wine text-white text-xs font-bold px-2 py-1 rounded">
              {tl('EN TENDANCE', 'IN TREND', 'DI TENDENZA')}
            </span>
          )}
        </div>
      </div>
      
      {/* Product Info */}
      <div className="p-4">
        <Link to={`/product/${product.id}`} className="block">
          <h3 className="font-medium text-lg mb-1 text-black hover:text-furniture-taupe transition-colors">
            {product.name}
          </h3>
          <p className="text-sm text-muted-foreground mb-2">
            {product.category}
          </p>
          <div className="flex justify-between items-center text-black">
            <span className="font-semibold">{tl('Prix sur demande', 'Price on request', 'Prezzo su richiesta')}</span>
            {(!product.stock || product.stock <= 0) && (
              <span className="text-destructive text-sm text-black">{tl('Rupture de stock', 'Out of stock', 'Esaurito')}</span>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default ProductCard;
